<template>
  <el-col :span="24" id="pos" v-loading.body="changingPage && !navigating" element-loading-text="Fetching page...">
    <el-row type="flex" justify="start" align="middle" id="pos-header">
      <HeaderInfoText :colspan="9" title="PROSCAN" :meta="meta" :selection="selection" />
      <el-col :span="6">
        <el-row type="flex" justify="center" v-if="meta.count > 20">
          <el-pagination
            layout="prev, pager, next, jumper"
            :total="meta.count"
            :page-size="20"
            @current-change="changePage"
            :current-page="currentPage"
          />
        </el-row>
      </el-col>
      <el-col :span="9" class="h-padded right-aligned-col">
        <div style="display:inline-block;">
          <el-button plain @click="toggleFilters" style="margin-left: 0;">
            {{ showFilters ? "Hide filters" : "Show filters" }}
          </el-button>
          <el-button plain @click="changePage(1)" style="margin-left: 0;">
            <b-icon icon="arrow-repeat" class="bicon" style="margin-right: 1px;"></b-icon> Refresh
          </el-button>
          <el-button
            :disabled="!hasCreatePermission"
            type="primary"
            icon="plus"
            @click="uploadDialogVisible = true">
            Upload New Document
          </el-button>
        </div>
      </el-col>
    </el-row>
    <el-row type="flex" justify="center" align="middle" style="padding: 0 12px;">
      <Filters
        ref="filters"
        :filters="filters"
        :filtersVisible="showFilters"
        :filterSize="4"
        :autoApply="false"
        clearAction="clearProscanLogState"
        clearMetadataAction="resetProscanLogMeta"
        searchAction="fetchProscanLogsWithQuery"
        transactionType="Proscan"
        :lineFieldSearch="null"
        @filterCleared="() => $refs.filters.applyFilters()"
        @doneFiltering="getAppliedFilters">
        <template v-for="(f, i) in filters" :slot="`${f.type}${i}`" slot-scope="props">
          <component :key="i" :is="f.type" :properties="f.props" :ref="`${f.type+'_ps_ref'}`" />
        </template>
      </Filters>
    </el-row>
    <el-row type="flex" justify="end" align="middle">
      <el-col>
        <el-row type="flex">
          <ProscanList
            v-if="proscanLogs"
            name="ps_transactions_list"
            ref="transactions_conf_list"
            :aggridColumns="aggridColumns"
            :rows="proscanLogs"
            :currentPage="currentPage"
            @openVendor="openVendorForm"
          />
        </el-row>
        <el-row v-if="!meta.count && !isFilterLoading" align="middle" justify="center" class="nodata-heading">
          No data available
        </el-row>
      </el-col>
    </el-row>
    <ProscanUploadDialog
      :visible.sync="uploadDialogVisible"
      :createdBatchId="createdBatchId"
      @uploaded="handleUploaded" />
    <!--
      Rendered here rather than inside the upload dialog: element-ui 1.4's el-dialog has no
      append-to-body, so a nested dialog stays inside the outer dialog's stacking context and
      ends up underneath its overlay, leaving the form unclickable.
    -->
    <NewBatch :hasCreatePermission="hasCreateBatchPermission" @populateFieldWithNewModel="handleBatchCreated" />
    <CustomerVendorForm type="vendor" :modelId="modelId" @updateModelIdProp="updateVendorModelId" />
  </el-col>
</template>

<script>
  import store from '@/store/store'
  import { mapGetters } from 'vuex'
  import Filters from '@/components/RetainedFilters.vue'
  import KeywordFilter from '@/components/FilterComponents/KeywordFilter'
  import HeaderInfoText from '@/components/HeaderInfoText'
  import { listViewMixins } from '@/Mixins/TransactionListViews'

  const POLL_INTERVAL = 5000
  import ProscanList from '@/components/SigmaClientNew/ProscanList.vue'
  import ProscanUploadDialog from './ProscanUploadDialog.vue'
  import NewBatch from '@/components/RouteComponents/Batches/NewBatch.vue'
  import CustomerVendorForm from '@/components/FormComponents/CustomerVendorForm.vue'
  import { BIcon, BIconArrowRepeat } from 'bootstrap-vue'

  export default {
    components: {
      Filters,
      KeywordFilter,
      HeaderInfoText,
      ProscanList,
      CustomerVendorForm,
      BIcon,
      BIconArrowRepeat,
      ProscanUploadDialog,
      NewBatch
    },
    mixins: [ listViewMixins ],
    data () {
      return {
        tableHeight: 500,
        showFilters: true,
        uploadDialogVisible: false,
        createdBatchId: '',
        pollTimer: null,
        modelId: null,
        aggridColumns: ['transactionIdColumn', 'documentTypeColumn', 'fileNameColumn', 'vendorColumn', 'batchColumn', 'statusColumn', 'infoColumn', 'uploadedAtColumn']
      }
    },
    methods: {
      /**
       * Opens the vendor behind a row in the vendor form. The form reads the vendor out of the
       * store and follows what it references - its addresses, accounts and the rest - so both are
       * in hand before it is shown one: the row carries an id and nothing else.
       * @param vendorId   The row's vendor_data.id
       */
      async openVendorForm (vendorId) {
        if (!vendorId) return
        try {
          this.$store.dispatch('fetchVendorsWithQuery', { id__in: [vendorId] })
            .then(async (r) => {
              if (r && r.count) {
                this.modelId = String(vendorId)
                this.$store.dispatch('changeModalVisibility', { entityType: 'customerVendorForm', visible: true, type: 'vendor' })
              }
            })
        } catch (e) {
          console.log(e)
        }
      },
      updateVendorModelId (id) {
        this.modelId = id
      },
      /* Hands a batch created through the New Batch modal to the upload dialog
       * @param payload  { type, getter, id }
       */
      handleBatchCreated (payload) {
        if (!(payload && payload.type === 'batch' && payload.id)) return
        this.createdBatchId = payload.id
      },
      /**
       * Reloads the list so the new rows show up, then polls them to completion
       * @param rows  The log rows the upload created, all IN_PROGRESS
       * @return {Promise<void>}
       */
      async handleUploaded (rows) {
        await this.changePage(1)
        this.startPolling()
      },
      /**
       * Refreshes the list while any row on it is still being extracted, so statuses and the
       * transactions they create appear without the user reloading. Only the polled rows are
       * merged in place - the page is not reloaded and nothing is announced.
       */
      startPolling () {
        this.stopPolling()
        if (!this.pendingLogs.length) return
        this.pollTimer = setInterval(async () => {
          const pending = this.pendingLogs
          if (!pending.length) return this.stopPolling()
          const rows = await this.$store.dispatch('fetchProscanLogsByIds', pending.map(row => row.log_id))
          if (rows.length) await this.$store.dispatch('updateProscanLogRows', rows)
          if (!this.pendingLogs.length) this.stopPolling()
        }, POLL_INTERVAL)
      },
      stopPolling () {
        if (this.pollTimer) {
          clearInterval(this.pollTimer)
          this.pollTimer = null
        }
      },
      getAppliedFilters () {
        this.$store.dispatch('clearSetProscanFilterInfo')
        this.$store.dispatch('setProscanFilterInfo', { page: this.currentPage, ...this.$refs.filters.getFilters })
      },
      /**
       * Handles paginating between AP pages
       * @param page  Target page
       */
      async changePage (page) {
        try {
          await this.$store.dispatch('setProscanFilterInfo', { ...this.$refs.filters.getFilters, ...{ page: page } })
          // Get FilterInfo from the store data
          let filterStoreData = this.getProscanFilterInfo
          if (Object.keys(filterStoreData).length) {
            this.filterRetained()
            return
          }
          this.changingPage = true
          const { currentFilters } = this
          let payload = currentFilters && Object.keys(currentFilters).length
            ? Object.assign({}, { query: { ...currentFilters, page } })
            : Object.assign({}, { query: { page }, storeLines: false })
          await this.$store.dispatch('clearProscanLogState', [])
          await this.$store.dispatch('setProscanFilterInfo', { page: page, ...this.currentFilters })
          const entities = await this.$store.dispatch('fetchProscanLogsWithQuery', payload)
          if (entities) {
            const promises = await this.$store.dispatch('findDependencies', entities)
            await Promise.all(promises)
          }
          await new Promise((resolve, reject) => {
            setTimeout(() => {
              this.changingPage = false
              resolve()
            })
          })
          this.$refs['transactions_conf_list'].restoreSelection(this.selection)
        } catch (e) {
          console.log(e) // TODO: use a logger
          this.changingPage = false
        }
      },
      async filterRetained () {
        this.changingPage = true
        // Get FilterInfo from the store data
        let filterStoreData = this.$store.getters.getProscanFilterInfo
        // Set status dropdown options (all statuses remain in the dropdown)
        if (!Object.keys(filterStoreData).length) {
          const query = {}
          for (let f in this.currentFilters) {
            if (!query.hasOwnProperty(f)) {
              query[f] = this.currentFilters[f]
            }
          }
          const entities = await store.dispatch('fetchProscanLogsWithQuery', { query })
          await store.dispatch('setFilter', query)
          await this.$store.dispatch('setProscanFilterInfo', { ...query, ...{ page: this.currentPage } })
          if (entities) {
            const promises = await this.$store.dispatch('findDependencies', entities)
            await Promise.all(promises)
          }
          this.changingPage = false
          return
        }
        try {
          const { currentFilters } = this
          // Prepare payload query to append to the API
          let payload = currentFilters && Object.keys(currentFilters).length
            ? Object.assign({}, { query: { ...currentFilters, page: filterStoreData['page'] ? filterStoreData['page'] : this.currentPage }, storeLines: false })
            : Object.assign({}, { query: { page: filterStoreData['page'] ? filterStoreData['page'] : this.currentPage } })
          // Check 'keyword' value is exist or not, if exists then assing the value
          if (filterStoreData && filterStoreData['keyword'] && filterStoreData['keyword'] !== '') {
            payload.query['search'] = filterStoreData['keyword']
          }
          // Reset AP data
          await Promise.all([
            store.dispatch('resetProscanLogMeta'),
            store.dispatch('clearProscanLogState', true)
          ])
          // Get AP's data based on the filter query
          const entities = await store.dispatch('fetchProscanLogsWithQuery', payload)
          this.$refs['transactions_conf_list'].restoreSelection(this.selection)
          if (entities) {
            const promises = await this.$store.dispatch('findDependencies', entities)
            await Promise.all(promises)
          }
          await new Promise((resolve, reject) => {
            setTimeout(() => {
              this.changingPage = false
              resolve()
            })
          })
          // Prefill the data for the filters
          if (this.filters && this.filters.length && this.$refs) {
            this.$nextTick(() => {
              // Check 'keywordFilter_ref' is exist or not, if exists then assing the value to prefill it
              if (this.$refs && this.$refs.keywordFilter_ps_ref && filterStoreData['keyword'] && filterStoreData['keyword'] !== '') {
                this.$refs.keywordFilter_ps_ref[0].keyword = filterStoreData['keyword']
                this.$refs.keywordFilter_ps_ref[0].change()
              }
            })
          }
        } catch (e) {
          console.log(e)
          this.changingPage = false
        }
      }
    },
    computed: {
      ...mapGetters({
        proscanLogs: 'getProscanLogs',
        meta: 'getProscanLogMetadata',
        currentPage: 'getCurrentProscanPage',
        getcurrentFilters: 'getFilters',
        getProscanFilterInfo: 'getProscanFilterInfo',
        checkIfUserHasRequiredPermissions: 'checkIfUserHasRequiredPermissions',
        checkIfUserHasAnyOfThePermissions: 'checkIfUserHasAnyOfThePermissions'
      }),
      width () {
        // Reducing by 3 % to handle sigma-frontend navbar
        return '100%'
      },
      height () {
        // Reducing by 3 % to handle sigma-frontend navbar
        return window.innerHeight * 0.63 + 'px'
      },
      hasCreatePermission () {
        return this.checkIfUserHasAnyOfThePermissions(['entry.add_accountspayable', 'entry.add_purchaseorder', 'system.add_vendor'])
      },
      hasCreateBatchPermission () {
        return this.checkIfUserHasRequiredPermissions(['entry.view_batch', 'entry.add_batch'])
      },
      /**
       * Rows on the current page that are still being extracted
       * @return {Array}
       */
      pendingLogs () {
        return this.proscanLogs.filter(row => row.status === 'IN_PROGRESS')
      },
      hasPermission () {
        return {
          read: this.hasReadPermission
        }
      },
      hasReadPermission () {
        return this.checkIfUserHasAnyOfThePermissions(['entry.view_accountspayable', 'entry.view_purchaseorder', 'system.view_vendor'])
      },
      actionFilters () {
        return []
      },
      filters () {
        return [
          {
            type: 'keywordFilter',
            props: {}
          }
        ]
      }
    },
    watch: {
      // The first page arrives after mount (beforeRouteEnter -> filterRetained), and later
      // pages can bring in rows still being extracted, so watch rather than poll on mount
      pendingLogs (rows) {
        if (rows.length && !this.pollTimer) this.startPolling()
      }
    },
    mounted () {
      const tableHeight = () => {
        const parentHeight = document.getElementById('pos').offsetHeight
        const headerHeight = document.getElementById('pos-header').offsetHeight
        return parentHeight - headerHeight
      }
      this.tableHeight = tableHeight()
    },
    beforeDestroy () {
      this.stopPolling()
      this.$store.dispatch('clearProscanLogState', true)
    },
    async beforeRouteEnter (to, from, next) {
      try {
        await Promise.all([
          store.dispatch('resetProscanLogMeta'),
          store.dispatch('clearProscanLogState', true)
        ])
      } catch (e) {
        console.log(e)
      } finally {
        next(vm => {
          vm.filterRetained()
        })
      }
    }
  }
</script>

<style scoped>
  #pos-header {
    border-bottom: 1px solid lightgray;
  }
  .h-padded {
    padding: 0 12px;
  }
  .left-aligned-col {
    text-align: left;
  }
  .right-aligned-col {
    text-align: right;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
  .nodata-heading {
    padding: 60px 0;
    color: #a7a7a7;
    font-size: 24px;
  }
  a, a:visited {
    text-decoration: none;
    color: rgb(0, 0, 238) !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/RouteComponents/Proscan/Proscan.vue